import { useEffect, useState } from "react";
import { Avatar, Box, Button, ColumnDatas, Table, Typography } from "fds";
import { useTranslation } from "react-i18next";
import "./index.scss";
import utilsCommon from "../../shared/utils/common";
import useAdminStorageStore from "../../redux/dispatcher/useAdminStorageStore";
import StorageDetailModal from "./StorageDetailModal";
import { StorageInfoDto } from "../../types/adminStorage";

const Storage: React.FC = () => {
  const { adminStorageStore, boundGetStorageList } = useAdminStorageStore();
  const [showDetailModal, toggleDetailModal] = useState(false);
  const [targetStorageKey, setTargetStorageKey] = useState("");
  const { t } = useTranslation();

  useEffect(() => {
    boundGetStorageList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!showDetailModal) {
      setTargetStorageKey("");
    }
  }, [showDetailModal]);

  const getStatusColor = (status: string): string => {
    switch (status) {
      case "R":
        return "#4C86F4";
      case "P":
        return "#EF5350";
      case "E":
      case "S":
        return "#FAA131";
      case "M":
        return "#797B82";
      case "C":
        return "#28292B";
      default:
        return "#FAA131";
    }
  };

  const getStatusText = (status: string): string => {
    switch (status) {
      case "R":
        return t("storage.status.active");
      case "P":
        return t("storage.status.inactive");
      case "E":
        return t("storage.status.connectionErr");
      case "S":
        return `${t("storage.status.connectionErr")}(${t(
          "storage.status.inactive"
        )})`;
      case "M":
        return t("storage.status.transferring");
      case "C":
        return t("storage.status.transferred");
      default:
        return t("storage.status.connectionErr");
    }
  };

  const checkStatus = (status: string) => {
    const color = getStatusColor(status);
    return (
      <Box alignItems="center">
        <svg
          width="6"
          height="6"
          viewBox="0 0 6 6"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle cx="3" cy="3" r="3" fill={color} />
        </svg>
        <Typography classes="mg ml-6" style={{ color }}>
          {getStatusText(status)}
        </Typography>
      </Box>
    );
  };

  const buildDefaultFlag = (item: StorageInfoDto) => {
    if (item.defaultFlag !== "Y") return null;
    return (
      <svg
        width="16px"
        height="16px"
        viewBox="0 0 14 14"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M2.74581 7.17085C2.51801 6.94305 2.14866 6.94305 1.92085 7.17085C1.69305 7.39866 1.69305 7.76801 1.92085 7.99581L4.83752 10.9125C5.06533 11.1403 5.43467 11.1403 5.66248 10.9125L12.0791 4.49581C12.307 4.26801 12.307 3.89866 12.0791 3.67085C11.8513 3.44305 11.482 3.44305 11.2542 3.67085L5.25 9.67504L2.74581 7.17085Z"
          fill={window.eco.companyName === "workspace" ? "#6623e8" : "#25c16f"}
        />
      </svg>
    );
  };

  const transformStorageList = (): ColumnDatas[] => {
    return adminStorageStore?.storageList?.map((item: StorageInfoDto) => ({
      data: [
        { columnText: "" },
        { columnText: buildDefaultFlag(item) },
        {
          columnText: (
            <Box alignItems="center">
              <Avatar
                name={item.name}
                shape="team"
                uniqueCode={item.serverKey}
              />
              <Typography classes="mg ml-8">{item.name}</Typography>
            </Box>
          ),
        },
        { columnText: item.url },
        { columnText: checkStatus(item.status) },
        {
          columnText: utilsCommon.getDateStr(item.createTime),
        },
        {
          columnText: (
            <Button
              type="secondary"
              size="sm"
              onClick={() => {
                setTargetStorageKey(item.serverKey);
                toggleDetailModal(true);
              }}
              label={t("storage.serverDetail")}
            />
          ),
        },
      ],
      id: item.serverKey,
    }));
  };

  return (
    <Box classes="storage" direction="column">
      {showDetailModal && (
        <StorageDetailModal
          serverKey={targetStorageKey}
          showDetailModal={showDetailModal}
          checkStatus={checkStatus}
          toggleDetailModal={toggleDetailModal}
        />
      )}
      <Typography type="h1" classes="mg mb-24">
        {t("storage.manageStorage")}
      </Typography>
      <Box classes="storage_body">
        <Table
          width="100%"
          height={
            adminStorageStore.storageList.length === 0
              ? "0px"
              : "calc(100vh - 222px)"
          }
          header={[
            { headerText: "", width: "2%" },
            { headerText: t("storage.default"), width: "5%" },
            { headerText: t("storage.serverName"), width: "43%" },
            { headerText: t("storage.serverAddress"), width: "20%" },
            { headerText: t("storage.serverStatus"), width: "10%" },
            { headerText: t("storage.serverDate"), width: "10%" },
            { headerText: "", width: "10%" },
          ]}
          columnHeight="md"
          data={transformStorageList()}
          totalCount={adminStorageStore.storageList.length}
          endReached={() => {}}
        />
      </Box>
    </Box>
  );
};

export default Storage;
